import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RiCheckLine, RiDownload2Line, RiLinkUnlinkM, RiLoader2Fill, RiSensorLine } from '@remixicon/react';
import { CONNECTOR_CHANNEL_PREFIX, type ConnectorDto, type SourceSensorDto } from '@atnt/shared';
import { Badge, Button, DataTable, EmptyState, IconButton, SegmentedControl, toast, type DataTableColumn, type DataTableGroup } from '@/shared/ui';
import { useDevices } from '@/features/devices/api';
import { ComboInput } from '@/features/devices/components/ComboInput';
import { ErrorBanner, NoResults, RegistryToolbar } from '@/features/devices/components/parts';
import { formatReading, matches } from '@/features/devices/format';
import { indicatorLabel, indicatorRefs, type IndicatorRef } from '@/features/devices/options';
import { useHotkey } from '@/features/settings/hotkeys';
import { useTableDensity } from '@/features/settings/preferences';
import type { ConnectorsContext } from '../backend';
import { useBindSourceSensor, useImportSourceSensors, useSourceSensors } from '../api';
import type { ConnectorRights } from '../permissions';
import { RelativeTime } from './parts';

type Filter = 'all' | 'bound' | 'unbound';
const STALE_MS = 3 * 3_600_000;

/**
 * "Datchiklar": the source's sensors grouped by node (unified server: node directory → CSV column with its last raw
 * value), filter bound / unbound, bind each to one of our physical indicators (ComboInput: "Qurilma · Ko'rsatkich"),
 * unbind, and bulk "Qurilma sifatida import qilish" for unbound ones (mocked).
 */
export function SensorsTab({ c, ctx, rights }: { c: ConnectorDto; ctx: ConnectorsContext; rights: ConnectorRights }) {
  const { t, i18n } = useTranslation('connectors');
  const density = useTableDensity();
  const searchRef = useRef<HTMLInputElement>(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const sensors = useSourceSensors(ctx, c.id);
  const devices = useDevices(ctx.tenant.id);
  const importer = useImportSourceSensors(ctx, c.id);
  const refs = indicatorRefs(devices.data, 'physical');
  const byId = new Map(refs.map((r) => [r.indicator.id, r]));
  const editable = rights.operate;

  useHotkey('search', () => searchRef.current?.focus());
  useHotkey('refresh', () => void sensors.refetch());

  const all = sensors.data ?? [];
  const boundCount = all.filter((s) => s.binding).length;
  const nodeLabel = (node: string) => (node === 'hydro-indicators' ? t('sensors.hydroNode') : node);
  const rows = all.filter((s) => {
    if (filter === 'bound' && !s.binding) return false;
    if (filter === 'unbound' && s.binding) return false;
    const ref = s.binding ? byId.get(s.binding.indicatorId) : undefined;
    return matches(s.columnName, search) || matches(nodeLabel(s.node), search) || matches(s.typeLabel, search) || matches(s.channel, search) || (ref ? matches(indicatorLabel(ref), search) : false);
  });
  const groups: DataTableGroup<SourceSensorDto>[] = [];
  for (const s of rows) {
    const last = groups[groups.length - 1];
    if (last && last.id === s.node) (last.rows as SourceSensorDto[]).push(s);
    else groups.push({ id: s.node, label: nodeLabel(s.node), rows: [s] });
  }
  const pickedUnbound = all.filter((s) => selected.has(s.id) && !s.binding);

  const runImport = async () => {
    try {
      const r = await importer.mutateAsync(pickedUnbound.map((s) => s.id));
      toast.success(t('sensors.imported', { devices: r.devicesCreated, count: r.indicatorsCreated }), { subtitle: t('sensors.importMock') });
      setSelected(new Set());
    } catch (err) {
      toast.error(t('error'), { subtitle: err instanceof Error ? err.message : undefined });
    }
  };

  const columns: DataTableColumn<SourceSensorDto>[] = [
    { id: 'column', header: t('sensors.column'), minWidth: 170, tone: 'primary', cell: (s) => <span className="truncate">{s.columnName}</span> },
    {
      id: 'type',
      header: t('sensors.type'),
      width: 220,
      tone: 'secondary',
      cell: (s) => (s.typeLabel ? <span className="truncate">{s.typeLabel}</span> : <span className="text-fg-tertiary">—</span>),
    },
    {
      id: 'channel',
      header: t('sensors.channel'),
      width: 160,
      cell: (s) =>
        s.channel?.toLowerCase().startsWith(CONNECTOR_CHANNEL_PREFIX) ? (
          <Badge color="accent">{s.channel}</Badge>
        ) : s.channel ? (
          <span className="truncate text-fg-secondary">{s.channel}</span>
        ) : (
          <span className="text-fg-tertiary">—</span>
        ),
    },
    {
      id: 'value',
      header: t('sensors.value'),
      width: 150,
      tone: 'primary',
      cell: (s) =>
        s.lastValue == null ? (
          <span className="text-fg-tertiary">—</span>
        ) : (
          <span className="truncate tabular-nums">
            {formatReading(s.lastValue, Math.abs(s.lastValue) >= 100 ? 1 : 2, i18n.language)}
            {s.unit && <span className="text-fg-secondary"> {s.unit}</span>}
          </span>
        ),
    },
    {
      id: 'time',
      header: t('sensors.time'),
      width: 140,
      tone: 'secondary',
      cell: (s) =>
        s.lastValueAt ? (
          <RelativeTime ts={s.lastValueAt} className={Date.now() - Date.parse(s.lastValueAt) > STALE_MS ? 'text-tone-lime' : undefined} />
        ) : (
          <span className="text-fg-tertiary">—</span>
        ),
    },
    {
      id: 'binding',
      header: t('sensors.binding'),
      width: 420,
      cell: (s) => <BindCell key={s.binding?.indicatorId ?? 'none'} ctx={ctx} connectorId={c.id} sensor={s} refs={refs} byId={byId} editable={editable} />,
    },
  ];

  return (
    <>
      <RegistryToolbar
        search={search}
        onSearch={setSearch}
        placeholder={t('sensors.search')}
        total={sensors.data ? rows.length : null}
        searchRef={searchRef}
        aside={
          editable ? (
            <Button size="md" variant="secondary" leftIcon={RiDownload2Line} disabled={pickedUnbound.length === 0} loading={importer.isPending} onClick={() => void runImport()}>
              {pickedUnbound.length ? t('sensors.importN', { count: pickedUnbound.length }) : t('sensors.import')}
            </Button>
          ) : null
        }
      >
        <SegmentedControl<Filter>
          appearance="pill"
          aria-label={t('sensors.filter')}
          value={filter}
          onValueChange={(v) => {
            setFilter(v);
            setSelected(new Set());
          }}
          items={[
            { value: 'all', label: t('sensors.all'), count: all.length },
            { value: 'bound', label: t('sensors.bound'), count: boundCount },
            { value: 'unbound', label: t('sensors.unbound'), count: all.length - boundCount },
          ]}
        />
      </RegistryToolbar>
      {sensors.isError ? (
        <ErrorBanner onRetry={() => void sensors.refetch()} />
      ) : (
        <DataTable
          aria-label={t('detail.tabs.sensors')}
          columns={columns}
          groups={groups}
          getRowId={(s) => s.id}
          selection={editable ? { selected, onChange: setSelected } : undefined}
          loading={sensors.isLoading}
          density={density}
          minWidth={1300}
          empty={
            search || filter !== 'all' ? (
              <NoResults
                onClear={() => {
                  setSearch('');
                  setFilter('all');
                }}
              />
            ) : (
              <EmptyState icon={RiSensorLine} title={t('sensors.empty')} description={t('sensors.emptyHint')} />
            )
          }
        />
      )}
    </>
  );
}

/** Inline binding editor: pick "Qurilma · Ko'rsatkich" (Enter / pick saves, empty + Enter unbinds, Esc reverts). */
function BindCell({
  ctx,
  connectorId,
  sensor,
  refs,
  byId,
  editable,
}: {
  ctx: ConnectorsContext;
  connectorId: string;
  sensor: SourceSensorDto;
  refs: readonly IndicatorRef[];
  byId: ReadonlyMap<string, IndicatorRef>;
  editable: boolean;
}) {
  const { t } = useTranslation('connectors');
  const bind = useBindSourceSensor(ctx, connectorId);
  const ref = sensor.binding ? byId.get(sensor.binding.indicatorId) : undefined;
  const current = ref ? indicatorLabel(ref) : '';
  const [draft, setDraft] = useState(current);
  const [done, setDone] = useState(false);
  useEffect(() => {
    if (!done) return;
    const h = window.setTimeout(() => setDone(false), 1500);
    return () => window.clearTimeout(h);
  }, [done]);

  if (!editable) {
    return current ? <span className="truncate font-medium text-fg">{current}</span> : <span className="text-fg-tertiary">{t('sensors.notBound')}</span>;
  }

  const options = refs.map(indicatorLabel);
  const commit = async (value: string) => {
    const next = value.trim();
    if (next === current) return;
    const target = next ? refs.find((r) => indicatorLabel(r) === next) : undefined;
    if (next && !target) {
      setDraft(current);
      return;
    }
    try {
      await bind.mutateAsync({ sensorId: sensor.id, indicatorId: target?.indicator.id ?? null });
      setDone(true);
      toast.success(target ? t('sensors.boundToast') : t('sensors.unboundToast'), { subtitle: `${sensor.columnName}${target ? ` → ${next}` : ''}` });
    } catch (err) {
      setDraft(current);
      toast.error(t('error'), { subtitle: err instanceof Error ? err.message : undefined });
    }
  };

  return (
    // oxlint-disable-next-line jsx-a11y/no-static-element-interactions -- wrapper commits the inline edit on blur / Enter; the combobox inside is the control
    <div
      className="flex w-full items-center gap-6"
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) void commit(draft);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          void commit(draft);
        }
        if (e.key === 'Escape') setDraft(current);
      }}
    >
      <ComboInput
        className="h-28"
        value={draft}
        onValueChange={(v) => {
          setDraft(v);
          if (v !== current && options.includes(v)) void commit(v);
        }}
        options={options}
        placeholder={t('sensors.bindPlaceholder')}
        aria-label={`${t('sensors.binding')}: ${sensor.columnName}`}
        maxLength={300}
      />
      <span className="flex size-16 shrink-0 items-center justify-center" aria-live="polite">
        {bind.isPending ? (
          <RiLoader2Fill aria-label={t('saving')} className="size-16 animate-spin text-icon" />
        ) : done ? (
          <RiCheckLine aria-label={t('sensors.boundToast')} className="size-16 text-tone-green" />
        ) : null}
      </span>
      <IconButton
        icon={RiLinkUnlinkM}
        size="xs"
        aria-label={`${t('sensors.unbind')}: ${sensor.columnName}`}
        tooltip
        disabled={!sensor.binding || bind.isPending}
        onClick={() => {
          setDraft('');
          void commit('');
        }}
      />
    </div>
  );
}
